import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Clock,
  User,
  Calendar,
  CheckCircle,
  Target,
  Users,
  TrendingUp,
  BookOpen,
  Trophy,
  Brain,
  Zap,
  BarChart3,
  Award,
  GraduationCap,
  HeartHandshake,
  Lightbulb,
  Star,
} from "lucide-react";
import coverTreinamento from "@/assets/cover-treinamento-de-vendas.jpg";
import imgEquipe from "@/assets/treinamento-vendas-equipe.jpg";
import imgPratica from "@/assets/treinamento-vendas-pratica.jpg";
import imgCoaching from "@/assets/treinamento-vendas-coaching.jpg";
import imgMetricas from "@/assets/treinamento-vendas-metricas.jpg";
import imgResultados from "@/assets/treinamento-vendas-resultados.jpg";
import juniorBianchim from "@/assets/junior-bianchim.jpg";
import postCoverFunil from "@/assets/cover-funil-vendas.jpg";
import postCoverNegociacao from "@/assets/cover-negociacao-b2b.jpg";
import postCoverEstrategias from "@/assets/cover-13-estrategias.jpg";

const TreinamentoVendas = () => {
  const navigate = useNavigate();

  const pilares = [
    { icon: Brain, title: "Mentalidade", desc: "Construção da postura comercial e resiliência diária." },
    { icon: BookOpen, title: "Técnica", desc: "Frameworks como SPIN, BANT, CHAMP e MEDDIC." },
    { icon: Users, title: "Processo", desc: "Funil, cadência e SLA entre etapas do pipeline." },
    { icon: BarChart3, title: "Métrica", desc: "Indicadores objetivos por vendedor e por etapa." },
    { icon: Trophy, title: "Cultura", desc: "Ritual de aprendizado, feedback e gamificação contínua." },
  ];

  const tipos = [
    {
      icon: GraduationCap,
      title: "Onboarding Comercial",
      desc: "Programa estruturado para acelerar a rampagem de novos vendedores em até 60 dias.",
    },
    {
      icon: HeartHandshake,
      title: "In Company Contínuo",
      desc: "Treinamentos práticos dentro da sua empresa, com cases reais do seu mercado.",
    },
    {
      icon: Zap,
      title: "Coaching Individual",
      desc: "Acompanhamento 1:1 para vendedores sênior e líderes comerciais.",
    },
  ];

  const beneficios = [
    { icon: TrendingUp, title: "+38% conversão", desc: "Vendedores treinados convertem mais leads em clientes." },
    { icon: Clock, title: "-50% rampagem", desc: "Novos vendedores atingem meta em menos da metade do tempo." },
    { icon: Star, title: "+92% retenção", desc: "Equipes treinadas têm rotatividade muito menor." },
    { icon: Award, title: "+27% ticket", desc: "Vendas mais consultivas elevam o ticket médio do time." },
  ];

  const metricas = [
    { label: "Taxa de Conversão por Etapa", desc: "Mede a saúde de cada estágio do funil e expõe gargalos." },
    { label: "Tempo Médio de Rampagem", desc: "Quantos dias até o novo vendedor bater meta consistente." },
    { label: "Ticket Médio", desc: "Valor médio fechado por venda e por vendedor." },
    { label: "Ciclo de Vendas", desc: "Dias entre primeiro contato e fechamento do contrato." },
    { label: "Taxa de No-show", desc: "Reuniões marcadas que não acontecem (qualifica o topo)." },
    { label: "Win Rate", desc: "Quantas oportunidades qualificadas viram cliente." },
  ];

  const cases = [
    {
      empresa: "Citróleo Group",
      resultado: "Equipe técnica comercial alinhada e processo unificado entre filiais.",
      img: imgEquipe,
    },
    {
      empresa: "Zentai Benefícios",
      resultado: "Mudança na postura comercial e novas estratégias de negociação.",
      img: imgCoaching,
    },
    {
      empresa: "Triplos Tecnologia",
      resultado: "Pipeline 40% mais enxuto e taxa de conversão 28% maior em 60 dias.",
      img: imgPratica,
    },
    {
      empresa: "Latina Eletrodomésticos",
      resultado: "Treinamento de força de vendas regional com aplicação imediata.",
      img: imgResultados,
    },
  ];

  const etapas = [
    { n: "01", t: "Diagnóstico Comercial", d: "Mapeamento do funil atual, dores e oportunidades." },
    { n: "02", t: "Desenho do Programa", d: "Conteúdo customizado por persona e nível de senioridade." },
    { n: "03", t: "Execução Prática", d: "Workshops, simulações e role-plays semanais." },
    { n: "04", t: "Coaching de Aplicação", d: "Acompanhamento individual no dia a dia comercial." },
    { n: "05", t: "Medição de Resultados", d: "Indicadores antes/depois com revisão mensal." },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Treinamento de Vendas: O Guia Definitivo Para Transformar Sua Equipe Comercial",
    description:
      "Guia completo sobre treinamento de vendas: pilares, tipos, metodologia, métricas, cases e etapas para estruturar um programa que gera resultado real.",
    image: "https://bianchimbusiness.com.br/cover-treinamento-de-vendas.jpg",
    datePublished: "2025-06-10",
    author: { "@type": "Person", name: "Junior Bianchim" },
    publisher: {
      "@type": "Organization",
      name: "Bianchim Business",
      logo: { "@type": "ImageObject", url: "https://bianchimbusiness.com.br/logo.png" },
    },
  };

  return (
    <>
      <SEOHead
        title="Treinamento de Vendas: Guia Definitivo Para Transformar Sua Equipe Comercial"
        description="Guia de treinamento de vendas e treinamento comercial: pilares, tipos, métricas e o passo a passo para treinar a equipe de vendas com previsibilidade."
        keywords="treinamento de vendas, treinamento comercial, treinamento de vendas comercial, treinamento da equipe de vendas, treinamento equipe comercial, capacitação de vendedores"
        canonicalUrl="https://bianchimbusiness.com.br/posts/treinamento-de-vendas"
        ogType="article"
        articlePublishedTime="2025-06-10"
        articleAuthor="Junior Bianchim"
      />
      <div className="min-h-screen bg-background">
        <Header />

        <main>
          <article>
            {/* HERO — 1 coluna */}
            <section className="relative">
              <div className="w-full h-[42vh] sm:h-[55vh] overflow-hidden">
                <img
                  src={coverTreinamento}
                  alt="Treinamento de vendas Bianchim Business com equipe certificada"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
              </div>
              <div className="container mx-auto px-4 -mt-32 relative z-10">
                <div className="max-w-4xl mx-auto text-center">
                  <span className="inline-block px-3 py-1 rounded-full bg-primary/15 text-primary text-xs font-semibold mb-4">
                    Treinamento Comercial
                  </span>
                  <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
                    Treinamento de Vendas: O Guia Definitivo Para Transformar Sua Equipe Comercial
                  </h1>
                  <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-2"><User className="w-4 h-4" /> Junior Bianchim</span>
                    <span className="flex items-center gap-2"><Calendar className="w-4 h-4" /> 10 Jun 2025</span>
                    <span className="flex items-center gap-2"><Clock className="w-4 h-4" /> 14 min de leitura</span>
                  </div>
                </div>
              </div>
            </section>

            {/* INTRODUÇÃO — 1 coluna */}
            <section className="py-12 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-3xl mx-auto prose prose-lg text-muted-foreground space-y-4">
                  <p>
                    <strong className="text-foreground">Treinamento de vendas</strong> deixou de ser
                    um evento pontual de fim de ano para virar o sistema operacional das equipes
                    comerciais que crescem de forma previsível. Empresas que tratam treinamento
                    como rotina, não como gasto, têm pipeline mais saudável, ciclos mais curtos e
                    vendedores que duram mais.
                  </p>
                  <p>
                    Neste guia você vai entender o que é, quais os pilares, os tipos, os benefícios
                    medidos, a metodologia, as métricas que importam, cases reais e o passo a passo
                    para implantar um programa de <strong className="text-foreground">treinamento
                    de vendas</strong> que sai do PowerPoint e chega no resultado.
                  </p>
                </div>
              </div>
            </section>

            {/* PILARES — 5 colunas */}
            <section className="py-12 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-5xl mx-auto text-center mb-10">
                  <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
                    Os 5 Pilares de Um Treinamento de Vendas Que Funciona
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Sem um destes pilares, o programa vira motivação sem método.
                  </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-6xl mx-auto">
                  {pilares.map((p, i) => (
                    <Card key={i} className="p-5 text-center border-primary/20 hover:border-primary/50 transition-colors">
                      <p.icon className="w-10 h-10 text-primary mx-auto mb-3" />
                      <h3 className="font-bold text-foreground mb-2">{p.title}</h3>
                      <p className="text-xs text-muted-foreground">{p.desc}</p>
                    </Card>
                  ))}
                </div>

                {/* CTA com menção à IA */}
                <Card className="mt-10 p-6 sm:p-8 max-w-4xl mx-auto bg-gradient-to-br from-primary/10 to-accent/10 border-primary/30">
                  <div className="flex items-start gap-4">
                    <Lightbulb className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-foreground mb-2">
                        Acelere os 5 pilares com IA aplicada ao treinamento
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        O <strong className="text-foreground">Vendas Trainer</strong> usa
                        inteligência artificial para simular clientes reais, gerar feedback
                        instantâneo, pontuar cada vendedor e gamificar o aprendizado. Treine seu
                        time 5x mais rápido, com método.
                      </p>
                      <Button onClick={() => navigate("/auth?mode=signup")} className="bg-primary text-primary-foreground hover:bg-primary/90">
                        Conhecer o Vendas Trainer com IA
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            </section>

            {/* TIPOS — 3 colunas */}
            <section className="py-12 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-5xl mx-auto">
                  <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-foreground text-center">
                    Tipos de Treinamento de Vendas
                  </h2>
                  <div className="grid md:grid-cols-3 gap-6">
                    {tipos.map((t, i) => (
                      <Card key={i} className="p-6 border-border hover:border-primary/40 transition-colors h-full">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                          <t.icon className="w-6 h-6 text-primary" />
                        </div>
                        <h3 className="text-xl font-bold text-foreground mb-3">{t.title}</h3>
                        <p className="text-muted-foreground">{t.desc}</p>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* IMAGEM EQUIPE — 1 coluna full */}
            <section className="py-6">
              <div className="container mx-auto px-4">
                <div className="max-w-5xl mx-auto rounded-2xl overflow-hidden">
                  <img
                    src={imgEquipe}
                    alt="Equipe comercial em treinamento de vendas presencial"
                    className="w-full h-auto object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </section>

            {/* BENEFÍCIOS — 4 colunas */}
            <section className="py-12 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-5xl mx-auto">
                  <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-foreground text-center">
                    Benefícios Medidos de Um Programa Estruturado
                  </h2>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                    {beneficios.map((b, i) => (
                      <div key={i} className="p-6 rounded-2xl bg-card border border-primary/20 text-center">
                        <b.icon className="w-8 h-8 text-primary mx-auto mb-3" />
                        <p className="text-2xl font-bold text-gradient-brand mb-2">{b.title}</p>
                        <p className="text-sm text-muted-foreground">{b.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* METODOLOGIA — 1/3 + 2/3 */}
            <section className="py-12 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8 items-center">
                  <div className="lg:col-span-1">
                    <img
                      src={imgPratica}
                      alt="Vendedor praticando pitch em sessão de role-play"
                      className="w-full h-auto rounded-2xl object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="lg:col-span-2">
                    <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                      Metodologia: Aprender Fazendo
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      Treinamento de vendas que funciona não é palestra. É um ciclo curto de teoria
                      mínima, prática intensa, feedback imediato e revisão dos números. Quanto mais
                      simulação, mais retenção. Quanto mais retenção, mais aplicação no dia a dia.
                    </p>
                    <ul className="space-y-3">
                      {[
                        "Workshops curtos (60–90 min) com aplicação no mesmo dia",
                        "Role-play semanal entre pares com checklist objetivo",
                        "Coaching 1:1 quinzenal por vendedor",
                        "Revisão mensal de indicadores antes/depois",
                      ].map((it, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-foreground">{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* MÉTRICAS — 2 colunas */}
            <section className="py-12 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-5xl mx-auto">
                  <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-foreground text-center">
                    Métricas Que Provam o ROI do Treinamento
                  </h2>
                  <div className="grid md:grid-cols-2 gap-5">
                    {metricas.map((m, i) => (
                      <Card key={i} className="p-5 border-border hover:border-primary/40 transition-colors flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <BarChart3 className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-bold text-foreground mb-1">{m.label}</h3>
                          <p className="text-sm text-muted-foreground">{m.desc}</p>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* CASES — Carrossel */}
            <section className="py-12 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-5xl mx-auto">
                  <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-foreground text-center">
                    Cases Reais de Quem Investiu em Treinamento
                  </h2>
                  <Carousel className="w-full">
                    <CarouselContent>
                      {cases.map((c, i) => (
                        <CarouselItem key={i} className="md:basis-1/2 lg:basis-1/3">
                          <Card className="overflow-hidden border-border h-full">
                            <div className="h-40 overflow-hidden">
                              <img src={c.img} alt={`Case de treinamento ${c.empresa}`} className="w-full h-full object-cover" loading="lazy" />
                            </div>
                            <div className="p-5">
                              <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-2">{c.empresa}</p>
                              <p className="text-sm text-muted-foreground">{c.resultado}</p>
                            </div>
                          </Card>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    <CarouselPrevious />
                    <CarouselNext />
                  </Carousel>
                </div>
              </div>
            </section>

            {/* ETAPAS — 2/3 + 1/3 */}
            <section className="py-12 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8 items-center">
                  <div className="lg:col-span-2 order-2 lg:order-1">
                    <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
                      As 5 Etapas Para Implantar Treinamento Comercial
                    </h2>
                    <div className="space-y-4">
                      {etapas.map((e, i) => (
                        <div key={i} className="flex gap-4 p-4 rounded-xl bg-card border border-border">
                          <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
                            <span className="text-primary font-bold">{e.n}</span>
                          </div>
                          <div>
                            <h3 className="font-bold text-foreground mb-1">{e.t}</h3>
                            <p className="text-sm text-muted-foreground">{e.d}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="lg:col-span-1 order-1 lg:order-2">
                    <img
                      src={imgMetricas}
                      alt="Dashboard de métricas de performance comercial após treinamento"
                      className="w-full h-auto rounded-2xl object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* CONCLUSÃO — 1 coluna */}
            <section className="py-12 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-3xl mx-auto">
                  <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
                    Treinamento de Vendas é Decisão de Gestão, Não de RH
                  </h2>
                  <div className="prose prose-lg text-muted-foreground space-y-4">
                    <p>
                      Empresas que dependem de "vendedor estrela" para bater meta são reféns dele. A
                      única forma de transformar resultado em sistema é estruturar
                      <strong className="text-foreground"> treinamento de vendas</strong> com método,
                      ritmo e métrica. Onboarding rápido, ciclos de aprendizado curtos, coaching
                      individual e medição honesta.
                    </p>
                    <p>
                      O treinamento certo paga a si mesmo em meses — em conversão, ticket e
                      retenção. O treinamento errado é PowerPoint caro. A diferença está no método.
                    </p>
                    <p>
                      <strong className="text-foreground">Comece pelo diagnóstico. Em 90 dias seu
                      time muda de patamar.</strong>
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* AUTOR */}
            <section className="py-10 sm:py-12 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <Card className="p-6 sm:p-8 border-primary/20 bg-gradient-to-br from-card to-primary/5">
                    <div className="flex flex-col sm:flex-row items-center gap-6">
                      <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-primary/30">
                        <img src={juniorBianchim} alt="Junior Bianchim" className="w-full h-full object-cover" />
                      </div>
                      <div className="text-center sm:text-left">
                        <h3 className="text-xl font-bold text-foreground mb-1">Junior Bianchim</h3>
                        <p className="text-primary font-medium text-sm mb-3">Especialista em Treinamento de Equipes Comerciais</p>
                        <p className="text-muted-foreground text-sm">
                          Mais de 20 anos estruturando operações comerciais para empresas como Citróleo Group,
                          Latina Eletrodomésticos, Mapfre Seguros e Zentai Benefícios. Fundador do Vendas Trainer.
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* POSTS RELACIONADOS — 3 colunas */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-5xl mx-auto">
                  <h2 className="text-2xl font-bold mb-8 text-foreground">Leitura Recomendada</h2>
                  <div className="grid sm:grid-cols-3 gap-6">
                    {[
                      { title: "Funil de Vendas em 2026", link: "/posts/funil-vendas-2026", img: postCoverFunil, time: "18 min" },
                      { title: "Negociação B2B em 2026", link: "/posts/negociacao-b2b-2026", img: postCoverNegociacao, time: "23 min" },
                      { title: "13 Estratégias de Vendas", link: "/posts/13-estrategias-vendas-2026", img: postCoverEstrategias, time: "25 min" },
                    ].map((p, i) => (
                      <Link key={i} to={p.link} className="block group">
                        <Card className="overflow-hidden border-border hover:border-primary/30 transition-all h-full">
                          <div className="h-36 overflow-hidden">
                            <img src={p.img} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                          </div>
                          <div className="p-4">
                            <h3 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors">{p.title}</h3>
                            <span className="text-xs text-muted-foreground mt-2 block">{p.time} de leitura</span>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* CTA FINAL */}
            <section className="py-16 sm:py-20 bg-muted/30">
              <div className="container mx-auto px-4">
                <Card className="p-8 sm:p-12 bg-gradient-to-br from-card via-primary/5 to-accent/10 border-primary/30 text-center max-w-4xl mx-auto">
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    <span className="text-gradient-brand">Quer Treinamento de Vendas Que Gera Resultado?</span>
                  </h2>
                  <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                    Fale com a Bianchim Business para um diagnóstico comercial gratuito ou crie sua
                    conta no Vendas Trainer e comece a treinar seu time hoje mesmo.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button size="lg" onClick={() => navigate("/auth?mode=signup")} className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow text-lg px-8 py-6 font-semibold">
                      Criar Conta Grátis
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                    <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-primary/50 hover:bg-primary/10" asChild>
                      <a href="https://wa.me/5516981960018" target="_blank" rel="noopener noreferrer">
                        Falar no WhatsApp
                      </a>
                    </Button>
                  </div>
                </Card>
              </div>
            </section>
          </article>

          <PageNavigation />
        </main>

        <Footer />
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
    </>
  );
};

export default TreinamentoVendas;
